/**
 * Status values for stock takes — stored on Firestore `status` field on each stock take document.
 *
 * ---------------------------------------------------------------------------
 * BACKEND — required coordination (Cloud Functions, workers, schedules, APIs)
 * ---------------------------------------------------------------------------
 * • **Do not write `PENDING` for stock takes.** The first active phase is
 *   {@link StockTakeStatus.inProgress} only. Creation, schedule runs, and any
 *   server-side stock-take bootstrap must set `IN_PROGRESS` (not `PENDING`).
 * • **One-time migration:** update every document where `status == "PENDING"`
 *   to `status == "IN_PROGRESS"` so counts, indexes, and dashboards match this
 *   app. Until that runs, this client still queries legacy `PENDING` rows
 *   together with `IN_PROGRESS` for the In Progress tab via
 *   {@link STOCK_TAKE_IN_PROGRESS_TAB_STATUSES}.
 * • **Lifecycle after migration:** `IN_PROGRESS` → `PREPARED` → `SUBMITTED` →
 *   `SUCCESS` (plus existing failure / cleanup statuses unchanged).
 * • A `PREPARED` stock take returns to `IN_PROGRESS` when a user moves it back,
 *   or when a pending line on that stock take is counted or removed.
 * • After migration is verified in production, you may remove the legacy
 *   `PENDING` branch from {@link STOCK_TAKE_IN_PROGRESS_TAB_STATUSES} and the
 *   extra count query for legacy rows in {@link StockTakeFirebaseService.getStockTakeStatusCounts}.
 */
export enum StockTakeStatus {
  inProgress = 'IN_PROGRESS',
  prepared = 'PREPARED',
  /** Sent to backend — awaiting confirmation */
  submitted = 'SUBMITTED',
  /** Backend confirmed the stock take was processed successfully */
  success = 'SUCCESS',
  /** Backend processing failed */
  failed = 'FAILED',
  /** Marked for backend cleanup — row should be greyed out and not interactive */
  deleting = 'DELETING',
}

/**
 * Removed first-phase status still present on unmigrated Firestore documents only.
 * Must equal the historical string `PENDING`.
 */
export const STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS = 'PENDING' as const;

/**
 * Firestore `in` query for the In Progress list tab — current phase plus legacy rows.
 */
export const STOCK_TAKE_IN_PROGRESS_TAB_STATUSES: readonly string[] = [
  StockTakeStatus.inProgress,
  STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
];

/** Statuses shown together on the Failed list tab (single Firestore `in` query). */
export const STOCK_TAKE_FAILED_TAB_STATUSES: readonly StockTakeStatus[] = [
  StockTakeStatus.failed,
];
